import type {
  DiscoveredDatapoint,
  DiscoveredDeal,
  DiscoveredFile,
  DiscoveredSighting,
  DiscoveredVerification,
  DiscoveredVerifyResult,
  DiscoveredVerifyStatus,
} from '@/services/discoveredDatapoints'
import { refusalOf } from '@/services/dataReview'
import type { SourceAnchor } from '@/services/ingestion'
import { plural } from './dataReviewFormat'
import { BAND, formatSample } from './discoveryFormat'

/**
 * What stands behind a found datapoint, in the form a person can check it: the
 * live deals that state it, the files it was read from, each reading, and what
 * re-reading those files found. Every number the row shows is one of these
 * lists counted, so each can be opened to the thing it counts.
 *
 * Pure data and pure functions, kept apart from the components.
 */

export interface DiscoveryEvidence {
  deals: DiscoveredDeal[]
  files: DiscoveredFile[]
  sightings: DiscoveredSighting[]
  /** The lists were rebuilt from the stored readings: an older server sends
   *  neither, and does not say which of the records they name still exist. */
  derived: boolean
  /** How many there are in all — more than the lists hold when the server
   *  capped them, so a count on screen is never just the length of a list. */
  totals: { deals: number; files: number; readings: number }
}

function dealsFromReadings(sightings: DiscoveredSighting[]): DiscoveredDeal[] {
  const deals = new Map<string, DiscoveredDeal>()
  // Stored oldest first; listed newest first, the way the server lists deals.
  for (const s of [...sightings].reverse()) {
    if (!s.ingestionId) continue
    const key = s.dealKey ?? s.ingestionId
    if (!deals.has(key)) {
      deals.set(key, { dealKey: key, loanId: s.loanId ?? null, ingestionId: s.ingestionId, name: null, receivedAt: null })
    }
  }
  return [...deals.values()]
}

function filesFromReadings(sightings: DiscoveredSighting[]): DiscoveredFile[] {
  const files = new Map<string, { deals: Set<string>; readings: number }>()
  for (const s of sightings) {
    if (!s.document) continue
    const file = files.get(s.document) ?? { deals: new Set<string>(), readings: 0 }
    file.readings += 1
    const deal = s.dealKey ?? s.ingestionId
    if (deal) file.deals.add(deal)
    files.set(s.document, file)
  }
  return [...files.entries()]
    .map(([document, f]) => ({ document, dealCount: f.deals.size, sightingCount: f.readings }))
    .sort((a, b) => b.dealCount - a.dealCount || b.sightingCount - a.sightingCount
      || a.document.localeCompare(b.document))
}

/** The deals, files and readings behind a row — as the server counted them,
 *  or, from a server that predates that, as its stored readings name them. */
export function evidenceOf(row: DiscoveredDatapoint): DiscoveryEvidence {
  const sightings = row.sightings ?? []
  const deals = row.deals ?? dealsFromReadings(sightings)
  const files = row.files ?? filesFromReadings(sightings)
  return {
    deals,
    files,
    sightings,
    derived: !row.deals,
    // The server's totals where it sends them — the deal count where it does
    // not — and never fewer than a list holds.
    totals: {
      deals: Math.max(row.dealTotal ?? row.dealCount ?? 0, deals.length),
      files: Math.max(row.fileTotal ?? 0, files.length),
      readings: Math.max(row.readingTotal ?? 0, sightings.length),
    },
  }
}

/** What a list holds of how many there are, when that is fewer — or null when
 *  it holds them all: "Listing 2 of 60 deals — 58 more not listed." */
export function listingNote(listed: number, total: number, noun: string): string | null {
  if (total <= listed) return null
  return `Listing ${listed} of ${plural(total, noun)} — ${total - listed} more not listed.`
}

/** Where a deal's evidence opens: its dossier once it is a deal, else the
 *  intake record with its review open — the links the rest of the app uses. */
export function dealHref(deal: { loanId: string | null; ingestionId: string }): string {
  return deal.loanId
    ? `/pipeline?deal=${encodeURIComponent(deal.loanId)}`
    : `/intake?focus=${encodeURIComponent(deal.ingestionId)}&review=1`
}

/** A deal's name on screen, else the id a person can search for. An intake
 *  record this account may not open is called that, by its id alone. */
export function dealName(deal: DiscoveredDeal): string {
  if (deal.redacted) return `Intake record ${deal.ingestionId}`
  return deal.name || deal.loanId || deal.ingestionId
}

/** Whether a link to this intake record could be refused: the server named it
 *  by id only, or — off the deal list while the server withholds some records
 *  from this account — it may be withheld too. */
function mayBeWithheld(evidence: DiscoveryEvidence, ingestionId: string): boolean {
  const deal = evidence.deals.find((d) => d.ingestionId === ingestionId)
  return deal ? Boolean(deal.redacted) : evidence.deals.some((d) => d.redacted)
}

/** Why a record is named by its id without a link, in a sentence — once per
 *  list that holds any. */
export function withheldNote(count: number): string | null {
  if (count === 0) return null
  return count === 1
    ? 'An intake record this account may not open is named by its id only, without a link.'
    : `${count} intake records this account may not open are named by their ids only, without links.`
}

/** Where a deal link on a found datapoint goes: its address, and the deal it
 *  opens when it is one (else it is an intake record). */
export interface EvidenceTarget {
  href: string
  loanId: string | null
}

/** A deal as a reading names it: what to call it, and where it opens — its
 *  dossier when `loanId` is set, else its intake record — or null when nothing does. */
export interface ReadingDeal {
  name: string
  href: string | null
  loanId: string | null
  /** An intake record this account may not open: named by id, never linked. */
  withheld?: true
}

/** Who a reading belongs to, and the link that opens it. A reading names its
 *  deal by key; one stored before keys were kept names only its record. */
export function readingDeal(
  evidence: DiscoveryEvidence,
  reading: { dealKey?: string | null; loanId?: string | null; ingestionId: string | null },
): ReadingDeal {
  const deal = (reading.dealKey && evidence.deals.find((d) => d.dealKey === reading.dealKey))
    || evidence.deals.find((d) => d.ingestionId === reading.ingestionId)
  if (deal?.redacted) return { name: dealName(deal), href: null, loanId: null, withheld: true }
  if (deal) return { name: dealName(deal), href: dealHref(deal), loanId: deal.loanId }
  if (!reading.ingestionId) return { name: 'Unnamed record', href: null, loanId: null }
  const loanId = reading.loanId ?? null
  // A deal's dossier is not intake's to refuse; an intake record may be.
  if (!loanId && mayBeWithheld(evidence, reading.ingestionId)) {
    return { name: `Intake record ${reading.ingestionId}`, href: null, loanId: null, withheld: true }
  }
  return {
    name: loanId ?? reading.ingestionId,
    href: dealHref({ loanId, ingestionId: reading.ingestionId }),
    loanId,
  }
}

/** Who a checked reading belongs to: the stored reading at the same place
 *  carries the deal key the check's answer does not. */
export function resultDeal(
  evidence: DiscoveryEvidence,
  result: DiscoveredVerifyResult,
): ReadingDeal {
  const reading = evidence.sightings.find((s) => s.ingestionId === result.ingestionId
    && s.document === result.document && s.sheet === result.sheet && s.cell === result.cell)
  return readingDeal(evidence, { dealKey: reading?.dealKey, loanId: reading?.loanId, ingestionId: result.ingestionId })
}

/**
 * A stored original a found datapoint was read from, as the source viewer opens
 * it — the way the review panel opens a record's own file: the record that
 * holds it, its content hash, its name, and where to open it.
 */
export interface StoredSource {
  ingestionId: string
  sha256: string
  filename: string
  anchor: SourceAnchor | null
}

const sameDocument = (a: string | null | undefined, b: string) =>
  (a ?? '').trim().toLowerCase() === b.trim().toLowerCase()

/** Where a listed file opens, or null when it cannot be: an older server names
 *  a file without its stored copy. It opens at the first cell that record read
 *  the label from, when a live reading says which. */
export function storedSource(evidence: DiscoveryEvidence, file: DiscoveredFile): StoredSource | null {
  if (!file.sha256 || !file.ingestionId) return null
  // The stored copy is read through its record, which would refuse this account.
  if (mayBeWithheld(evidence, file.ingestionId)) return null
  const reading = evidence.sightings.find((s) => s.ingestionId === file.ingestionId
    && sameDocument(s.document, file.document) && s.sheet && s.cell)
  return {
    ingestionId: file.ingestionId,
    sha256: file.sha256,
    filename: file.document,
    anchor: reading ? { cell: `${reading.sheet}!${reading.cell}`, sha256: file.sha256 } : null,
  }
}

/** Where in its file a reading sat: `sheet!cell`, or whichever of the two is known. */
export function placeOf(reading: { sheet: string | null; cell: string | null }): string {
  if (reading.sheet && reading.cell) return `${reading.sheet}!${reading.cell}`
  return reading.sheet || reading.cell || '—'
}

/** What adding a row teaches the engine when its field exists: nothing is
 *  created, but every deal's next reading takes the word as that field. */
function wordFor(row: DiscoveredDatapoint): string {
  return `“${row.displayLabel}” as a word for ${row.proposedLabel || row.proposedName} on every deal`
}

/**
 * Why Add is held back on a row, in a sentence — or null when it is not.
 *
 * The rule the server holds an accept to, for a new field and a new word
 * alike: two live deals, two distinct files, and a score out of the weak band.
 * Either changes what the engine does on every deal; one deal's workbook,
 * however often it was re-read, is not a reason to.
 */
export function addBlockedReason(row: DiscoveredDatapoint, evidence: DiscoveryEvidence): string | null {
  const notEnough = 'one deal or one file is not enough evidence to '
    + (row.fieldExists ? `teach the engine ${wordFor(row)}` : 'add a field every deal will record')
  const deals = row.dealCount ?? 0
  if (deals < 2) return `Stated on ${plural(deals, 'live deal')} — ${notEnough}.`
  const files = evidence.totals.files
  if (files < 2) return `Read from ${files === 1 ? 'one file' : 'no live file'} — ${notEnough}.`
  if (row.confidence.band !== 'weak') return null
  return 'Thin evidence — seen rarely, or the values do not hold together (the score says which). '
    + (row.fieldExists
      ? `Teaching the engine ${wordFor(row)} needs more than this.`
      : 'A field every deal will record needs more than this.')
}

/** Whether a row's label, or a file it was read from, holds the text. */
export function matchesFoundFilter(row: DiscoveredDatapoint, text: string): boolean {
  const needle = text.trim().toLowerCase()
  if (!needle) return true
  const evidence = evidenceOf(row)
  return [
    row.displayLabel, row.label, row.proposedLabel, row.proposedName,
    ...evidence.files.map((f) => f.document),
    ...evidence.sightings.map((s) => s.document),
  ].some((value) => (value ?? '').toLowerCase().includes(needle))
}

export interface FileGroup<T> {
  /** Null for the rows no live reading names a file for. */
  document: string | null
  items: T[]
}

/** Rows filed under every file each was read from — a label two files state
 *  is in both — the file behind the most labels first, the unfiled last. */
export function groupFoundByFile<T>(items: T[], rowOf: (item: T) => DiscoveredDatapoint): FileGroup<T>[] {
  const groups = new Map<string | null, T[]>()
  for (const item of items) {
    const documents = [...new Set(evidenceOf(rowOf(item)).files.map((f) => f.document))]
    for (const document of documents.length > 0 ? documents : [null]) {
      groups.set(document, [...(groups.get(document) ?? []), item])
    }
  }
  return [...groups.entries()]
    .map(([document, grouped]) => ({ document, items: grouped }))
    .sort((a, b) => {
      if (a.document === null || b.document === null) return a.document === null ? 1 : -1
      return b.items.length - a.items.length || a.document.localeCompare(b.document)
    })
}

/** How each answer of a check against the source reads, in the bands' own colours. */
export const VERIFY_META: Record<DiscoveredVerifyStatus, { label: string; chip: string }> = {
  confirmed: { label: 'Confirmed', chip: BAND.auto.chip },
  label_moved: { label: 'Label moved', chip: BAND.review.chip },
  value_mismatch: { label: 'Value differs', chip: BAND.review.chip },
  source_unavailable: { label: 'Source unavailable', chip: BAND.weak.chip },
  not_checked: { label: 'Not checked', chip: BAND.weak.chip },
}

export function verifyMeta(status: string): { label: string; chip: string } {
  return VERIFY_META[status as DiscoveredVerifyStatus]
    ?? { label: status.replace(/_/g, ' '), chip: BAND.weak.chip }
}

function valueText(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'number' || typeof value === 'string') return formatSample(value)
  return typeof value === 'object' ? JSON.stringify(value) : String(value)
}

/** What a check found at one reading beyond its badge, or null when the badge
 *  says it all. A value that differs is named with the label the file states
 *  beside it — when the label is gone and another sits at the cell, the number
 *  is that label's, and saying so keeps it from passing as this one's. */
export function verifyDetail(result: DiscoveredVerifyResult): string | null {
  const absent = result.foundValue === null || result.foundValue === undefined
  switch (result.status) {
    case 'confirmed':
      return `reads ${valueText(result.foundValue ?? result.expectedValue)}`
    case 'value_mismatch':
      if (!result.foundLabel) {
        return `expected ${valueText(result.expectedValue)} — the file no longer states the label`
          + (absent ? '' : ` (the cell holds ${valueText(result.foundValue)})`)
      }
      return `expected ${valueText(result.expectedValue)}, found ${valueText(result.foundValue)} `
        + `beside “${result.foundLabel}”`
    case 'label_moved': {
      if (result.foundSheet || result.foundCell) {
        const now = placeOf({ sheet: result.foundSheet ?? null, cell: result.foundCell ?? null })
        if (absent) return `now at ${now}`
        // The check says where the label went, not that its value held: what
        // it reads there is set beside what was recorded when the two differ.
        const found = valueText(result.foundValue)
        const recorded = valueText(result.expectedValue)
        return `now at ${now}, reads ${found}` + (found !== recorded ? ` (recorded ${recorded})` : '')
      }
      return `no longer at ${placeOf(result)}`
    }
    default:
      // Not read: the server says why, in its own words, or the badge says it alone.
      return result.reason ?? null
  }
}

/** What a busy server's 429 means for the reviewer, when it gives no words of its own. */
export const CHECK_BUSY = 'Another check is running; try again in a moment.'

/**
 * Why a check against the source was not made, as the row says it. The server
 * runs only so many checks at once and answers the rest 429: that is a wait,
 * said in the server's words (or ours, when it sends none), not a failure. Any
 * other refusal is a failure, with the server's reason.
 */
export function checkRefusal(error: unknown): { busy: boolean; message: string } {
  const refusal = refusalOf(error)
  if (refusal.status !== 429) {
    return { busy: false, message: `Could not check against the source: ${refusal.message}` }
  }
  const detail = (error as { response?: { data?: { detail?: unknown } } } | null)?.response?.data?.detail
  const said = typeof detail === 'string' ? detail
    : typeof (detail as { message?: unknown } | null)?.message === 'string' ? (detail as { message: string }).message
      : ''
  return { busy: true, message: said.trim() || CHECK_BUSY }
}

/** A check against the source, in one line: how many readings, and how each
 *  came out. An outcome that did not happen is left out. */
export function verifySummary(verification: DiscoveredVerification): string {
  const s = verification.summary
  const parts = [
    s.confirmed ? `${s.confirmed} confirmed` : null,
    s.labelMoved ? `${s.labelMoved} ${s.labelMoved === 1 ? 'label moved' : 'labels moved'}` : null,
    s.valueMismatch ? `${s.valueMismatch} ${s.valueMismatch === 1 ? 'value differs' : 'values differ'}` : null,
    s.unavailable ? `${s.unavailable} ${s.unavailable === 1 ? 'source unavailable' : 'sources unavailable'}` : null,
    s.notChecked ? `${s.notChecked} not checked` : null,
  ].filter((part): part is string => part !== null)
  const head = `Checked ${plural(verification.checked, 'reading')} against the stored originals`
  return parts.length > 0 ? `${head} — ${parts.join(' · ')}.` : `${head}.`
}
